/**
 * eLearning API Service
 * 
 * Service layer for all eLearning API calls.
 * Connects to backend Express API server for database persistence.
 */

// Use /api for eLearning endpoints (proxied to the Express server; see PORT / vite.config.ts)
// VITE_API_BASE_URL is for charts API, so we hardcode /api for eLearning
const API_BASE_URL = '/api';

import {
  Course,
  Module,
  Quiz,
  QuizQuestion,
  Enrollment,
  ModuleCompletion,
  QuizAttempt,
  Certification,
  UserGamification,
  UserBadge,
  UserAchievement,
  CourseFilters,
  CreateCourseInput,
  UpdateCourseInput,
  CreateModuleInput,
  UpdateModuleInput,
  CreateQuizInput,
  CreateQuestionInput,
  QuizAnswers,
  CourseStatus,
} from '../types/elearning';
import { clerkAuthHeaders } from './clerkApiAuth';

// ============================================
// API SERVICE CLASS
// ============================================

class ELearningService {
  // ============================================
  // COURSES
  // ============================================

  /**
   * Fetch all courses with optional filters
   */
  async fetchCourses(filters?: CourseFilters): Promise<Course[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.search) params.append('search', filters.search);
      if (filters?.difficulty) params.append('difficulty', filters.difficulty);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.audiences?.length) params.append('audiences', filters.audiences.join(','));
      if (filters?.topics?.length) params.append('topics', filters.topics.join(','));
      if (filters?.clinicalAreas?.length) params.append('clinicalAreas', filters.clinicalAreas.join(','));

      const url = `${API_BASE_URL}/courses${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, {
        headers: await clerkAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch courses: ${response.statusText}`);
      }

      const courses = await response.json();
      return courses;
    } catch (error) {
      console.error('Error fetching courses:', error);
      throw error;
    }
  }

  /**
   * Fetch a single course by slug
   */
  async fetchCourse(slug: string): Promise<Course | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/courses/slug/${slug}`, {
        headers: await clerkAuthHeaders(),
      });
      
      if (response.status === 404) {
        return null;
      }
      
      if (!response.ok) {
        throw new Error(`Failed to fetch course: ${response.statusText}`);
      }

      const course = await response.json();
      return course;
    } catch (error) {
      console.error('Error fetching course:', error);
      throw error;
    }
  }

  /**
   * Fetch a single course by ID
   */
  async fetchCourseById(id: string): Promise<Course | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/courses/${id}?answers=edit`, {
        headers: await clerkAuthHeaders(),
      });
      
      if (response.status === 404) {
        return null;
      }
      
      if (!response.ok) {
        throw new Error(`Failed to fetch course: ${response.statusText}`);
      }

      const course = await response.json();
      return course;
    } catch (error) {
      console.error('Error fetching course:', error);
      throw error;
    }
  }

  /**
   * Create a new course (Admin only)
   */
  async createCourse(input: CreateCourseInput): Promise<Course> {
    try {
      const response = await fetch(`${API_BASE_URL}/courses`, {
        method: 'POST',
        headers: await clerkAuthHeaders(),
        body: JSON.stringify({
          ...input,
          slug: input.slug || input.title.toLowerCase().replace(/\s+/g, '-'),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `Failed to create course: ${response.statusText}`);
      }

      const course = await response.json();
      return course;
    } catch (error) {
      console.error('Error creating course:', error);
      throw error;
    }
  }

  /**
   * Update an existing course (Admin only)
   */
  async updateCourse(courseId: string, updates: UpdateCourseInput): Promise<Course> {
    try {
      const response = await fetch(`${API_BASE_URL}/courses/${courseId}`, {
        method: 'PUT',
        headers: await clerkAuthHeaders(),
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `Failed to update course: ${response.statusText}`);
      }

      const course = await response.json();
      return course;
    } catch (error) {
      console.error('Error updating course:', error);
      throw error;
    }
  }

  /**
   * Delete a course (Admin only)
   */
  async deleteCourse(courseId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/courses/${courseId}`, {
        method: 'DELETE',
        headers: await clerkAuthHeaders({ json: false }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `Failed to delete course: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting course:', error);
      throw error;
    }
  }

  // ============================================
  // MODULES
  // ============================================

  /**
   * Fetch a module by course slug and module slug
   */
  async fetchModule(courseSlug: string, moduleSlug: string): Promise<Module | null> {
    try {
      const course = await this.fetchCourse(courseSlug);
      if (!course) return null;

      const module = course.modules?.find((m) => m.slug === moduleSlug);
      if (!module) return null;

      // Load quiz if exists
      if (module.hasQuiz) {
        const quiz = await this.fetchQuizByModuleId(module.id);
        if (quiz) {
          return { ...module, quiz };
        }
      }

      return module;
    } catch (error) {
      console.error('Error fetching module:', error);
      return null;
    }
  }

  /**
   * Create a new module (Admin only)
   */
  async createModule(courseId: string, input: CreateModuleInput): Promise<Module> {
    try {
      const response = await fetch(`${API_BASE_URL}/modules`, {
        method: 'POST',
        headers: await clerkAuthHeaders(),
        body: JSON.stringify({
          ...input,
          courseId,
          slug: input.slug || input.title.toLowerCase().replace(/\s+/g, '-'),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `Failed to create module: ${response.statusText}`);
      }

      const module = await response.json();
      return module;
    } catch (error) {
      console.error('Error creating module:', error);
      throw error;
    }
  }

  /**
   * Update an existing module (Admin only)
   */
  async updateModule(moduleId: string, updates: UpdateModuleInput): Promise<Module> {
    try {
      const response = await fetch(`${API_BASE_URL}/modules/${moduleId}`, {
        method: 'PUT',
        headers: await clerkAuthHeaders(),
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `Failed to update module: ${response.statusText}`);
      }

      const module = await response.json();
      return module;
    } catch (error) {
      console.error('Error updating module:', error);
      throw error;
    }
  }

  /**
   * Delete a module (Admin only)
   */
  async deleteModule(moduleId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/modules/${moduleId}`, {
        method: 'DELETE',
        headers: await clerkAuthHeaders({ json: false }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `Failed to delete module: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting module:', error);
      throw error;
    }
  }

  /**
   * Reorder modules within a course (Admin only)
   */
  async reorderModules(
    _courseId: string,
    moduleOrders: { id: string; order: number }[]
  ): Promise<void> {
    try {
      // Update each module's order
      await Promise.all(
        moduleOrders.map(({ id, order }) =>
          this.updateModule(id, { order })
        )
      );
    } catch (error) {
      console.error('Error reordering modules:', error);
      throw error;
    }
  }

  // ============================================
  // ENROLLMENT
  // ============================================

  /**
   * Enroll in a course
   */
  async enrollInCourse(courseId: string): Promise<Enrollment> {
    const response = await fetch(`${API_BASE_URL}/enrollments`, {
      method: 'POST',
      headers: await clerkAuthHeaders(),
      body: JSON.stringify({ courseId }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to enroll in course');
    }

    return response.json();
  }

  /**
   * Unenroll from a course
   */
  async unenrollFromCourse(courseId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/enrollments/${courseId}`, {
      method: 'DELETE',
      headers: await clerkAuthHeaders({ json: false }),
    });

    if (!response.ok && response.status !== 404) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to unenroll from course');
    }
  }

  /**
   * Get the signed-in user's enrollments
   */
  async getUserEnrollments(): Promise<Enrollment[]> {
    const response = await fetch(`${API_BASE_URL}/enrollments`, {
      headers: await clerkAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to load enrollments');
    }

    return response.json();
  }

  // ============================================
  // PROGRESS
  // ============================================

  /**
   * Complete a module
   */
  async completeModule(enrollmentId: string, moduleId: string): Promise<ModuleCompletion> {
    const response = await fetch(
      `${API_BASE_URL}/enrollments/${enrollmentId}/modules/${moduleId}/complete`,
      {
        method: 'POST',
        headers: await clerkAuthHeaders(),
        body: JSON.stringify({}),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to complete module');
    }

    return response.json();
  }

  // ============================================
  // QUIZZES
  // ============================================

  /**
   * Submit a quiz attempt
   */
  async submitQuizAttempt(quizId: string, answers: QuizAnswers): Promise<QuizAttempt> {
    const response = await fetch(`${API_BASE_URL}/quizzes/${quizId}/attempts`, {
      method: 'POST',
      headers: await clerkAuthHeaders(),
      body: JSON.stringify({ answers }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to submit quiz');
    }

    return response.json();
  }

  /**
   * Fetch quiz attempts for an enrollment
   */
  async fetchQuizAttempts(quizId: string): Promise<QuizAttempt[]> {
    const response = await fetch(`${API_BASE_URL}/quizzes/${quizId}/attempts`, {
      headers: await clerkAuthHeaders({ json: false }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to fetch quiz attempts');
    }
    return response.json();
  }

  // ============================================
  // CERTIFICATIONS
  // ============================================

  /**
   * Fetch user's certifications
   */
  async fetchCertifications(): Promise<Certification[]> {
    const response = await fetch(`${API_BASE_URL}/certifications`, {
      headers: await clerkAuthHeaders({ json: false }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to fetch certifications');
    }
    return response.json();
  }

  // ============================================
  // GAMIFICATION
  // ============================================

  /**
   * Get user's gamification data
   */
  async getUserGamification(): Promise<UserGamification> {
    await new Promise((resolve) => setTimeout(resolve, 200));

    // Get from localStorage or return default
    try {
      const stored = localStorage.getItem('elearning_gamification');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch {
      // Fall through to default
    }

    return {
      id: 'gamification-1',
      userId: 'mock-user-id',
      totalPoints: 0,
      currentStreak: 0,
      longestStreak: 0,
      badges: [],
      achievements: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }

  /**
   * Award points to a user
   */
  async awardPoints(_userId: string, points: number, _reason: string): Promise<UserGamification> {
    await new Promise((resolve) => setTimeout(resolve, 300));

    const gamification = await this.getUserGamification();
    gamification.totalPoints += points;
    gamification.updatedAt = new Date().toISOString();

    localStorage.setItem('elearning_gamification', JSON.stringify(gamification));

    return gamification;
  }

  /**
   * Check and award badges based on user progress
   */
  async checkAndAwardBadges(_userId: string): Promise<UserBadge[]> {
    await new Promise((resolve) => setTimeout(resolve, 300));
    // Mock implementation - would check various criteria
    return [];
  }

  /**
   * Check and award achievements
   */
  async checkAndAwardAchievements(_userId: string): Promise<UserAchievement[]> {
    await new Promise((resolve) => setTimeout(resolve, 300));
    // Mock implementation
    return [];
  }

  /**
   * Update user's learning streak
   */
  async updateStreak(_userId: string): Promise<UserGamification> {
    await new Promise((resolve) => setTimeout(resolve, 200));

    const gamification = await this.getUserGamification();
    const today = new Date().toISOString().split('T')[0];
    const lastActivity = gamification.lastActivityDate?.split('T')[0];

    if (lastActivity === today) {
      // Already updated today
      return gamification;
    }

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    if (lastActivity === yesterdayStr) {
      // Continue streak
      gamification.currentStreak += 1;
    } else {
      // Reset streak
      gamification.currentStreak = 1;
    }

    if (gamification.currentStreak > gamification.longestStreak) {
      gamification.longestStreak = gamification.currentStreak;
    }

    gamification.lastActivityDate = new Date().toISOString();
    gamification.updatedAt = new Date().toISOString();

    localStorage.setItem('elearning_gamification', JSON.stringify(gamification));

    return gamification;
  }

  // ============================================
  // ADMIN - COURSE BUILDER
  // ============================================

  /**
   * Save course as draft (Admin only)
   */
  async saveCourseDraft(courseId: string, _content: any): Promise<Course> {
    // Save as draft is just an update with DRAFT status
    return this.updateCourse(courseId, { status: CourseStatus.DRAFT });
  }

  /**
   * Publish a course (Admin only)
   */
  async publishCourse(courseId: string): Promise<Course> {
    return this.updateCourse(courseId, { status: CourseStatus.PUBLISHED });
  }

  /**
   * Unpublish a course (Admin only)
   */
  async unpublishCourse(courseId: string): Promise<Course> {
    return this.updateCourse(courseId, { status: CourseStatus.DRAFT });
  }

  // ============================================
  // ADMIN - QUIZ BUILDER
  // ============================================

  /**
   * Fetch a quiz by module ID (Admin only)
   */
  async fetchQuizByModuleId(moduleId: string, options?: { forEdit?: boolean }): Promise<Quiz | null> {
    try {
      const editQuery = options?.forEdit ? '?answers=edit' : '';
      const response = await fetch(`${API_BASE_URL}/quizzes/module/${moduleId}${editQuery}`, {
        headers: await clerkAuthHeaders(),
      });
      
      if (response.status === 404) {
        return null;
      }
      
      if (!response.ok) {
        throw new Error(`Failed to fetch quiz: ${response.statusText}`);
      }

      const quiz = await response.json();
      return quiz;
    } catch (error) {
      console.error('Error fetching quiz:', error);
      return null;
    }
  }

  /**
   * Create a quiz (Admin only)
   */
  async createQuiz(moduleId: string, input: CreateQuizInput): Promise<Quiz> {
    try {
      const response = await fetch(`${API_BASE_URL}/quizzes`, {
        method: 'POST',
        headers: await clerkAuthHeaders(),
        body: JSON.stringify({
          ...input,
          moduleId,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `Failed to create quiz: ${response.statusText}`);
      }

      const quiz = await response.json();
      return quiz;
    } catch (error) {
      console.error('Error creating quiz:', error);
      throw error;
    }
  }

  /**
   * Add a question to a quiz (Admin only)
   */
  async addQuestion(quizId: string, input: CreateQuestionInput): Promise<QuizQuestion> {
    try {
      const response = await fetch(`${API_BASE_URL}/quizzes/${quizId}/questions`, {
        method: 'POST',
        headers: await clerkAuthHeaders(),
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `Failed to add question: ${response.statusText}`);
      }

      const question = await response.json();
      return question;
    } catch (error) {
      console.error('Error adding question:', error);
      throw error;
    }
  }

  /**
   * Update a question (Admin only)
   */
  async updateQuestion(questionId: string, input: CreateQuestionInput): Promise<QuizQuestion> {
    try {
      // Extract quizId from questionId or find it
      // For now, we'll need to pass quizId separately or find it
      // This is a limitation - in production you'd have a direct endpoint
      const response = await fetch(`${API_BASE_URL}/quizzes/questions/${questionId}`, {
        method: 'PUT',
        headers: await clerkAuthHeaders(),
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `Failed to update question: ${response.statusText}`);
      }

      const question = await response.json();
      return question;
    } catch (error) {
      console.error('Error updating question:', error);
      throw error;
    }
  }

  /**
   * Delete a question (Admin only)
   */
  async deleteQuestion(questionId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/quizzes/questions/${questionId}`, {
        method: 'DELETE',
        headers: await clerkAuthHeaders({ json: false }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `Failed to delete question: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting question:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const elearningAPI = new ELearningService();

